import React, { useEffect, useState, useCallback } from "react";
import { PageLoader, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { EnvelopeSimple, MagnifyingGlass, ArrowClockwise } from "@phosphor-icons/react";
import api from "../../lib/api";

const STATUS_STYLE = {
  sent: "bg-blue-500/15 text-blue-300 border border-blue-500/25",
  delivered: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25",
  bounced: "bg-red-500/15 text-red-300 border border-red-500/25",
  complained: "bg-orange-500/15 text-orange-300 border border-orange-500/25",
  delayed: "bg-amber-500/15 text-amber-300 border border-amber-500/25",
  failed: "bg-red-500/15 text-red-300 border border-red-500/25",
  blocked: "bg-zinc-500/15 text-zinc-300 border border-zinc-500/25",
};

const StatChip = ({ label, value, active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`rounded-xl border px-4 py-3 text-left transition-colors ${active ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/40"}`}
  >
    <div className="text-2xl font-semibold tracking-tight">{value ?? 0}</div>
    <div className="text-xs text-muted-foreground capitalize">{label}</div>
  </button>
);

export default function AdminEmails() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [kinds, setKinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    if (kind) params.kind = kind;
    if (q.trim()) params.q = q.trim();
    return api.get("/admin/email-logs", { params })
      .then((r) => { setLogs(r.data.logs); setStats(r.data.stats); setKinds(r.data.kinds || []); })
      .finally(() => setLoading(false));
  }, [status, kind, q]);

  useEffect(() => { load(); }, [status, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div data-testid="admin-emails-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-1">Deliverability</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Email Log</h1>
          <p className="text-sm text-muted-foreground mt-1">Every email sent via Resend — IDs, delivery status and bounces.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="email-refresh-btn"><ArrowClockwise className="mr-2" size={16} /> Refresh</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <StatChip label="total" value={stats.total} active={!status} onClick={() => setStatus("")} testid="email-stat-total" />
        {["sent", "delivered", "bounced", "complained", "failed", "blocked"].map((s) => (
          <StatChip key={s} label={s} value={stats[s]} active={status === s} onClick={() => setStatus(status === s ? "" : s)} testid={`email-stat-${s}`} />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                 placeholder="Search recipient, subject or Resend ID…" className="pl-9" data-testid="email-search-input" />
        </div>
        <Select value={kind || "all"} onValueChange={(v) => setKind(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="email-kind-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {kinds.map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={load} data-testid="email-search-btn">Search</Button>
      </div>

      {loading ? <PageLoader /> : logs.length === 0 ? (
        <EmptyState icon={EnvelopeSimple} title="No emails yet" subtitle="Sent emails and their delivery status will appear here." testid="no-emails" />
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Recipient</th>
                  <th className="px-4 py-3 font-medium">Subject</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Resend ID</th>
                  <th className="px-4 py-3 font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-border align-top" data-testid={`email-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLE[l.status] || "bg-muted text-foreground"}`}>{l.status}</span>
                      {l.error && <div className="text-xs text-red-500 mt-1 max-w-[220px] truncate" title={l.error}>{l.error}</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{l.to}</td>
                    <td className="px-4 py-3 max-w-[280px]"><div className="truncate" title={l.subject}>{l.subject}</div></td>
                    <td className="px-4 py-3 capitalize whitespace-nowrap">{l.kind}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{l.resend_id || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmt(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
